import React, { useMemo, useState } from 'react';
import { useStore, useBrandById, useModelById } from '../store-hybrid';
import { BrandLogo } from '../components/BrandLogo';
import { BookingCalendar } from '../components/BookingCalendar';
import { ReturnCarModal } from '../components/ReturnCarModal';
import { CustomerInfoModal } from '../components/CustomerInfoModal';
import { ExtendRentalModal } from '../components/ExtendRentalModal';
import { parseDate, calculateDays } from '../utils/dateUtils';
import { formatSimpleCurrency } from '../utils/currencyUtils';
import { handleImageError } from '../utils/imageUtils';
import { Car } from '../types';

export const RentalPage: React.FC = () => {
  const { cars, brands, fund, rentCar, returnCar } = useStore();
  const [filterBrand, setFilterBrand] = useState('');
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [selectedCarForRent, setSelectedCarForRent] = useState<Car | null>(null);
  const [selectedCarForReturn, setSelectedCarForReturn] = useState<Car | null>(null);
  const [showBookingCalendar, setShowBookingCalendar] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showCustomerInfoModal, setShowCustomerInfoModal] = useState(false);
  const [pendingRentalData, setPendingRentalData] = useState<{
    car: Car;
    startDate: Date;
    endDate: Date;
    startTime: string;
    endTime: string;
  } | null>(null);
  
  // Get brand and model info for pending rental (hooks must be called unconditionally)
  const pendingCarBrand = useBrandById(pendingRentalData?.car.brandId || '');
  const pendingCarModel = useModelById(pendingRentalData?.car.modelId || '');

  // Filter cars available for rental (in garage, has daily rate, not rented, not sold, and paid)
  const availableCars = useMemo(() => {
    let filtered = cars.filter(car => 
      car.inGarage && 
      car.dailyRate && 
      car.dailyRate > 0 && 
      !car.isRented && 
      !car.isSold &&
      car.isPaid // Only paid cars can be rented
    );
    
    if (filterBrand) {
      filtered = filtered.filter(car => car.brandId === filterBrand);
    }
    
    return filtered;
  }, [cars, filterBrand]);

  // Get currently rented cars
  const rentedCars = useMemo(() => {
    return cars.filter(car => car.isRented);
  }, [cars]);

  // Calculate total revenue from rental history + advance payments from currently rented cars
  const totalRevenue = useMemo(() => {
    // Revenue from completed rentals (rental history)
    const historyRevenue = cars.reduce((total, car) => {
      if (car.rentalHistory) {
        return total + car.rentalHistory.reduce((carTotal, record) => {
          // totalAmount already includes additional charges, no need to add them again
          return carTotal + record.totalAmount;
        }, 0);
      }
      return total;
    }, 0);

    // Revenue from advance payments (currently rented cars with payment type 'advance')
    const advancePayments = fund?.transactions
      ?.filter(t => 
        t.category === 'rental' && 
        t.type === 'add_money' && 
        t.description?.includes('Rental Payment (Advance)')
      )
      .reduce((sum, t) => sum + t.amount, 0) || 0;

    return historyRevenue + advancePayments;
  }, [cars, fund?.transactions]);

  const handleRentCar = (car: Car) => {
    setSelectedCarForRent(car);
    setShowBookingCalendar(true);
  };

  const handleBookingConfirm = (startDate: Date, endDate: Date, startTime: string, endTime: string) => {
    if (selectedCarForRent) {
      // Store rental data and show customer info modal
      setPendingRentalData({
        car: selectedCarForRent,
        startDate,
        endDate,
        startTime,
        endTime
      });
      
      setShowBookingCalendar(false);
      setShowCustomerInfoModal(true);
    }
  };

  const handleReturnCar = (car: Car) => {
    setSelectedCarForReturn(car);
    setShowReturnModal(true);
  };

  const handleReturnConfirm = (newMileage: number, additionalCharges: any[]) => {
    if (selectedCarForReturn) {
      returnCar(selectedCarForReturn.id, newMileage, additionalCharges);
    }
    
    setShowReturnModal(false);
    setSelectedCarForReturn(null);
  };

  const handleCustomerInfoConfirm = (customerInfo: { name: string; contact: string; email: string; license: string; paymentType: 'advance' | 'after' }) => {
    if (pendingRentalData) {
      const { car, startDate, endDate, startTime, endTime } = pendingRentalData;
      
      // Combine date and time
      const [startHour, startMinute] = startTime.split(':').map(Number);
      const [endHour, endMinute] = endTime.split(':').map(Number);
      
      const fullStartDate = new Date(startDate);
      fullStartDate.setHours(startHour, startMinute, 0, 0);
      
      const fullEndDate = new Date(endDate);
      fullEndDate.setHours(endHour, endMinute, 0, 0);
      
      // Rent car with customer information and payment type
      rentCar(car.id, fullStartDate, fullEndDate, customerInfo.name, customerInfo.contact, customerInfo.email, customerInfo.license, customerInfo.paymentType);
    }
    
    setShowCustomerInfoModal(false);
    setSelectedCarForRent(null);
    setPendingRentalData(null);
  };

  const handleModalCancel = () => {
    setShowBookingCalendar(false);
    setShowReturnModal(false);
    setShowCustomerInfoModal(false);
    setSelectedCarForRent(null);
    setSelectedCarForReturn(null);
    setPendingRentalData(null);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">🚗 Rental Dashboard</h1>
        <p className="text-gray-600 dark:text-gray-400">Manage car rentals with booking calendar and mileage tracking</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Available for Rent</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{availableCars.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Currently Rented</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{rentedCars.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatSimpleCurrency(totalRevenue)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Currently Rented Cars */}
      {rentedCars.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Currently Rented Cars</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rentedCars.map(car => (
              <RentedCarCard 
                key={car.id} 
                car={car} 
                onReturn={() => handleReturnCar(car)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Collapsible Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 mb-6 overflow-hidden">
        {/* Filter Header - Always Visible */}
        <div 
          className="p-4 sm:p-6 cursor-pointer hover:bg-gray-50 dark:bg-gray-900 transition-colors border-b border-gray-100 dark:border-gray-700"
          onClick={() => setFiltersExpanded(!filtersExpanded)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Filters</h3>
              {!filtersExpanded && (
                <div className="flex items-center space-x-2 text-sm text-gray-500">
                  <span className="px-2 py-1 bg-gray-100 rounded-full text-xs">
                    {filterBrand ? brands.find(b => b.id === filterBrand)?.name : 'All Brands'}
                  </span>
                </div>
              )}
            </div>
            <svg 
              className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${filtersExpanded ? 'rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        {/* Filter Content - Expandable */}
        <div className={`transition-all duration-300 ease-in-out ${filtersExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className="p-4 sm:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Filter by Brand
                </label>
                <select
                  value={filterBrand}
                  onChange={(e) => setFilterBrand(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">All Brands</option>
                  {brands.map(brand => (
                    <option key={brand.id} value={brand.id}>{brand.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Clear Filters Button */}
            {filterBrand && (
              <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                <button
                  onClick={() => setFilterBrand('')}
                  className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
                >
                  Clear all filters
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Available Cars */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Available for Rent</h2>
        
        {availableCars.length === 0 ? (
          <div className="text-center py-12">
            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9L18 10c-.1-.1-.4-.6-1.4-1.3C16.1 8.4 14.7 8 13 8H9.5c-.6 0-1.1.4-1.4.9L6.8 11H4c-.6 0-1 .4-1 1v3c0 .6.4 1 1 1h2c0 1.7 1.3 3 3 3s3-1.3 3-3h4c0 1.7 1.3 3 3 3s3-1.3 3-3z"/>
            </svg>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No cars available for rent</h3>
            <p className="text-gray-600 dark:text-gray-400">Add cars to garage with daily rates to make them available for rent.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {availableCars.map(car => (
              <RentalCarCard 
                key={car.id} 
                car={car} 
                onBookRental={() => handleRentCar(car)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Booking Calendar Modal */}
      <BookingCalendar
        isOpen={showBookingCalendar}
        onDateSelect={handleBookingConfirm}
        onCancel={handleModalCancel}
      />

      {/* Return Car Modal */}
      {selectedCarForReturn && (
        <ReturnCarModal
          isOpen={showReturnModal}
          car={selectedCarForReturn}
          onReturn={handleReturnConfirm}
          onCancel={handleModalCancel}
        />
      )}

      {/* Customer Info Modal */}
      <CustomerInfoModal
        isOpen={showCustomerInfoModal && !!pendingRentalData}
        onClose={handleModalCancel}
        onConfirm={handleCustomerInfoConfirm}
        carInfo={{
          brand: pendingCarBrand?.name || '',
          model: pendingCarModel?.name || '',
          plate: pendingRentalData?.car.plate,
          dailyRate: pendingRentalData?.car.dailyRate
        }}
        rentalDates={pendingRentalData ? {
          startDate: pendingRentalData.startDate,
          endDate: pendingRentalData.endDate,
          startTime: pendingRentalData.startTime,
          endTime: pendingRentalData.endTime
        } : {
          startDate: new Date(),
          endDate: new Date(),
          startTime: '09:00',
          endTime: '17:00'
        }}
      />
    </div>
  );
};

// Rented Car Card Component
const RentedCarCard: React.FC<{ car: Car; onReturn: () => void }> = ({ car, onReturn }) => {
  const brand = useBrandById(car.brandId);
  const model = useModelById(car.modelId);
  const { extendRental } = useStore();
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const hasMultipleImages = car.images && car.images.length > 1;
  const totalImages = car.images?.length || 0;

  const nextImage = () => {
    if (hasMultipleImages) {
      setCurrentImageIndex((prev) => (prev + 1) % totalImages);
    }
  };

  const prevImage = () => {
    if (hasMultipleImages) {
      setCurrentImageIndex((prev) => (prev - 1 + totalImages) % totalImages);
    }
  };
  
  const daysRented = car.rentedFrom ? calculateDays(car.rentedFrom, new Date()) : 0;
  const isOverdue = car.rentedUntil && new Date() > parseDate(car.rentedUntil);
  
  // Enhanced rental status for rented cars
  const getRentalStatus = () => {
    if (!car.rentedUntil) return null;
    
    const now = new Date();
    const rentedUntil = parseDate(car.rentedUntil);
    const msUntilReturn = rentedUntil.getTime() - now.getTime();
    const hoursUntilReturn = msUntilReturn / (1000 * 60 * 60);
    
    if (hoursUntilReturn < 0) {
      return { type: 'overdue', hours: Math.abs(hoursUntilReturn), urgent: true };
    } else if (hoursUntilReturn <= 1) {
      return { type: 'due-very-soon', hours: hoursUntilReturn, urgent: true };
    } else if (hoursUntilReturn <= 6) {
      return { type: 'due-soon', hours: hoursUntilReturn, urgent: false };
    } else if (hoursUntilReturn <= 24) {
      return { type: 'due-today', hours: hoursUntilReturn, urgent: false };
    }
    return null;
  };

  const rentalStatus = getRentalStatus();

  const handleExtendRental = (newEndDate: Date, newEndTime: string) => {
    extendRental(car.id, newEndDate, newEndTime);
    setShowExtendModal(false);
  };
  
  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border p-6 relative ${
      rentalStatus?.type === 'overdue' ? 'border-red-300 bg-red-50' : 
      rentalStatus?.type === 'due-very-soon' ? 'border-red-200 bg-red-25' :
      rentalStatus?.type === 'due-soon' ? 'border-orange-200 bg-orange-25' :
      rentalStatus?.type === 'due-today' ? 'border-yellow-200 bg-yellow-25' :
      'border-gray-100 dark:border-gray-700'
    }`}>
      {/* Enhanced Status Notification */}
      {rentalStatus && (
        <div className={`absolute top-3 right-3 px-2 py-1 rounded-lg text-xs font-bold shadow-md z-10 ${
          rentalStatus.type === 'overdue' 
            ? 'bg-red-600 text-white animate-pulse'
            : rentalStatus.type === 'due-very-soon'
              ? 'bg-red-500 text-white animate-bounce'
              : rentalStatus.type === 'due-soon'
                ? 'bg-orange-500 text-white'
                : 'bg-yellow-500 text-white'
        }`}>
          {rentalStatus.type === 'overdue' 
            ? `🚨 ${Math.floor(Math.abs(rentalStatus.hours))}h OVERDUE`
            : rentalStatus.type === 'due-very-soon'
              ? `⚠️ ${Math.max(1, Math.floor(rentalStatus.hours * 60))}m LEFT`
              : `⏰ ${Math.floor(rentalStatus.hours)}h LEFT`
          }
        </div>
      )}

      {/* Car Image */}
      <div className="w-full h-32 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 rounded-xl mb-4 overflow-hidden relative group">
        {car.images && car.images.length > 0 ? (
          <>
            <img 
              src={car.images[currentImageIndex]} 
              alt={`${brand?.name} ${model?.name}`}
              className="w-full h-full object-cover transition-opacity duration-300"
              onError={(e) => handleImageError(e, 'car', 400, 300)}
            />
            
            {/* Navigation Buttons */}
            {hasMultipleImages && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-opacity-70"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                
                <button
                  onClick={nextImage}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-opacity-70"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                
                {/* Image Counter */}
                <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  {currentImageIndex + 1}/{totalImages}
                </div>
              </>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <svg className="w-16 h-16 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm0 2h12v8H4V6z" clipRule="evenodd" />
            </svg>
          </div>
        )}
      </div>

      <div className="flex items-center space-x-3 mb-4">
        <BrandLogo brandId={car.brandId} size="md" />
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-white">
            {brand?.name} {model?.name}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">{car.plate}</p>
        </div>
      </div>
      
      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-500">Rented Until:</span>
          <span className={`font-medium ${isOverdue ? 'text-red-600' : ''}`}>
            {car.rentedUntil ? parseDate(car.rentedUntil).toLocaleDateString() : 'N/A'}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Days Rented:</span>
          <span className="font-medium">{daysRented} days</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Start Mileage:</span>
          <span className="font-medium">{car.rentalStartMileage?.toLocaleString()} km</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Current Revenue:</span>
          <span className="font-medium text-green-600">
            {formatSimpleCurrency((car.dailyRate || 0) * daysRented)}
          </span>
        </div>
      </div>
      
      {/* Action Buttons */}
      <div className="mt-4 space-y-2">
        {/* Extend Rental Button - Show when there's a rental status notification */}
        {rentalStatus && (
          <button
            onClick={() => setShowExtendModal(true)}
            className={`w-full px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
              rentalStatus.type === 'overdue' 
                ? 'bg-red-600 text-white hover:bg-red-700 animate-pulse shadow-lg'
                : rentalStatus.type === 'due-very-soon'
                  ? 'bg-red-500 text-white hover:bg-red-600 animate-bounce shadow-md'
                  : rentalStatus.type === 'due-soon'
                    ? 'bg-orange-500 text-white hover:bg-orange-600 shadow-md'
                    : 'bg-yellow-500 text-white hover:bg-yellow-600 shadow-sm'
            }`}
          >
            {rentalStatus.type === 'overdue' 
              ? '🚨 Extend Overdue Rental'
              : rentalStatus.type === 'due-very-soon'
                ? '⚠️ Extend Urgent'
                : '⏰ Extend Rental'
            }
          </button>
        )}
        
        {/* Return Car Button */}
        <button
          onClick={onReturn}
          className="w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          📋 Return Car
        </button>
      </div>

      {/* Extend Rental Modal */}
      {car.rentedUntil && (
        <ExtendRentalModal
          isOpen={showExtendModal}
          onClose={() => setShowExtendModal(false)}
          onExtend={handleExtendRental}
            currentEndDate={parseDate(car.rentedUntil)}
          carInfo={{
            brand: brand?.name || '',
            model: model?.name || '',
            plate: car.plate
          }}
        />
      )}
    </div>
  );
};

// Rental Car Card Component - Optimized for rental page layout
const RentalCarCard: React.FC<{ car: Car; onBookRental: () => void }> = ({ car, onBookRental }) => {
  const brand = useBrandById(car.brandId);
  const model = useModelById(car.modelId);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const hasMultipleImages = car.images && car.images.length > 1;
  const totalImages = car.images?.length || 0;

  const nextImage = () => {
    if (hasMultipleImages) {
      setCurrentImageIndex((prev) => (prev + 1) % totalImages);
    }
  };

  const prevImage = () => {
    if (hasMultipleImages) {
      setCurrentImageIndex((prev) => (prev - 1 + totalImages) % totalImages);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 hover:shadow-md transition-shadow">
      {/* Car Image */}
      <div className="w-full h-32 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl mb-4 overflow-hidden relative group">
        {car.images && car.images.length > 0 ? (
          <>
            <img 
              src={car.images[currentImageIndex]} 
              alt={`${brand?.name} ${model?.name}`}
              className="w-full h-full object-cover transition-opacity duration-300"
              onError={(e) => handleImageError(e, 'car', 400, 300)}
            />
            
            {/* Navigation Buttons */}
            {hasMultipleImages && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-opacity-70"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                
                <button
                  onClick={nextImage}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-opacity-70"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                
                {/* Image Counter */}
                <div className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  {currentImageIndex + 1}/{totalImages}
                </div>
              </>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <svg className="w-16 h-16 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm0 2h12v8H4V6z" clipRule="evenodd" />
            </svg>
          </div>
        )}
      </div>

      {/* Car Info */}
      <div className="space-y-3">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3">
            <BrandLogo brandId={car.brandId} size="md" />
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white text-lg">
                {brand?.name} {model?.name}
              </h3>
              {car.year && (
                <p className="text-sm text-gray-500">{car.year} • {car.color}</p>
              )}
            </div>
          </div>
          
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Available
          </span>
        </div>

        {/* Car Details */}
        <div className="grid grid-cols-2 gap-2 text-sm">
          {car.plate && (
            <div className="flex items-center space-x-1">
              <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
              <span className="text-gray-600 dark:text-gray-400">{car.plate}</span>
            </div>
          )}
          
          {car.mileageKm && (
            <div className="flex items-center space-x-1">
              <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <span className="text-gray-600 dark:text-gray-400">{car.mileageKm.toLocaleString()} km</span>
            </div>
          )}

          {car.transmission && (
            <div className="flex items-center space-x-1">
              <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
              </svg>
              <span className="text-gray-600 dark:text-gray-400 capitalize">{car.transmission}</span>
            </div>
          )}

          {car.fuel && (
            <div className="flex items-center space-x-1">
              <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.618 5.984A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <span className="text-gray-600 dark:text-gray-400 capitalize">{car.fuel}</span>
            </div>
          )}
        </div>

        {/* Pricing - More prominent for rental */}
        <div className="pt-3 border-t border-gray-100 dark:border-gray-700">
          <div className="flex justify-between items-center">
            {car.dailyRate && (
              <div>
                <span className="text-2xl font-bold text-primary-600">{formatSimpleCurrency(car.dailyRate || 0)}</span>
                <span className="text-gray-500 ml-1">/day</span>
              </div>
            )}
            {car.salePrice && (
              <div className="text-right">
                <div className="text-xs text-gray-500">Sale Price</div>
                <div className="text-sm font-semibold text-green-600">
                  {formatSimpleCurrency(car.salePrice || 0)}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Book Rental Button */}
        <button
          onClick={onBookRental}
          className="w-full px-4 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors font-medium"
        >
          📅 Book Rental
        </button>
      </div>
    </div>
  );
};